/**
 * Feature Event Utilities
 *
 * Convenient functions for tracking application-specific feature events
 * Used throughout the app to track user interactions and feature usage
 *
 * User Story: US4 - Automatic Event Tracking
 */
let _capture = null;
/**
 * Register the PostHog capture function.
 *
 * Must be called once at app initialisation (after consent is established)
 * before any track* helpers will fire. The supplied function should already
 * handle consent-gating so events are not sent when the user has opted out.
 *
 * Pass `null` to unregister (e.g. on logout / consent withdrawal).
 */
export function registerCapture(fn) {
    _capture = fn;
}
// Type guard for browser environment
function isBrowser() {
    return typeof window !== 'undefined';
}
function capture(event, properties) {
    if (isBrowser()) {
        _capture?.(event, properties);
    }
}
/**
 * Track review initiated event
 *
 * Fired when user starts a new code review
 */
export function trackReviewInitiated(properties) {
    capture('review_initiated', {
        ...properties,
        initiation_source: properties?.['initiation_source'] ?? 'manual',
    });
}
/**
 * Fired when onboarding finishes in the UI (SCM + repos path or summary finish),
 * after POST /onboarding/complete succeeds.
 */
export function trackOnboardingCompletedFrontend(properties) {
    capture('onboarding_completed_frontend', properties);
}
/**
 * Track review completed event
 *
 * Fired when user completes a code review
 */
export function trackReviewCompleted(properties) {
    capture('review_completed', properties);
}
/**
 * Track PR analyzed event
 *
 * Fired when PR analysis completes
 */
export function trackPRAnalyzed(properties) {
    capture('pr_analyzed', properties);
}
/**
 * Track report generated event
 *
 * Fired when user generates a report
 */
export function trackReportGenerated(properties) {
    capture('report_generated', properties);
}
/**
 * Track setting modified event
 *
 * Fired when user changes application settings
 */
export function trackSettingModified(properties) {
    capture('setting_modified', properties);
}
/**
 * Track collaboration invited event
 *
 * Fired when user invites collaborators
 */
export function trackCollaborationInvited(properties) {
    capture('collaboration_invited', properties);
}
/**
 * Track export requested event
 *
 * Fired when user exports data
 */
export function trackExportRequested(properties) {
    capture('export_requested', properties);
}
/**
 * Track search executed event
 *
 * Fired when user performs a search
 */
export function trackSearchExecuted(properties) {
    capture('search_executed', properties);
}
/**
 * Track filter applied event
 *
 * Fired when user applies filters
 */
export function trackFilterApplied(properties) {
    capture('filter_applied', properties);
}
/**
 * Track sort applied event
 *
 * Fired when user changes sort order
 */
export function trackSortApplied(properties) {
    capture('sort_applied', properties);
}
export function trackInboxViewed(properties) {
    capture('inbox_viewed', properties);
}
export function trackInboxFilterChanged(properties) {
    capture('inbox_filter_changed', properties);
}
export function trackInboxPrimaryOpen(properties) {
    capture('inbox_primary_open', properties);
}
export function trackInboxMarkDone(properties) {
    capture('inbox_mark_done', properties);
}
export function trackInboxSnooze(properties) {
    capture('inbox_snooze', properties);
}
export function trackPaletteOpened(properties) {
    capture('palette_opened', properties);
}
export function trackPaletteDismissedWithoutSelection(properties) {
    capture('palette_dismissed_without_selection', properties);
}
export function trackPaletteEntrySelected(properties) {
    capture('palette_entry_selected', properties);
}
export function trackDevelopPhaseListViewed(properties) {
    capture('develop_phase_list_viewed', properties);
}
export function trackWorkItemDetailViewed(properties) {
    capture('work_item_detail_viewed', properties);
}
export function trackPullRequestDetailViewed(properties) {
    capture('pull_request_detail_viewed', properties);
}
/**
 * PR intent findings panel (DC-352) — PostHog-safe: `organization_id` and opaque `pr_id` only.
 */
export function trackPullRequestIntentFindingsViewed(properties) {
    capture('pull_request_intent_findings_viewed', properties);
}
export function trackAdrDetailViewed(properties) {
    capture('adr_detail_viewed', properties);
}
export function trackGovernDemoSurfaceViewed(properties) {
    capture('govern_demo_surface_viewed', properties);
}
export function trackInspectDemoSurfaceViewed(properties) {
    capture('inspect_demo_surface_viewed', properties);
}
export function trackGovernAiControlsSummaryViewed(properties) {
    capture('govern_ai_controls_summary_viewed', properties);
}
export function trackExecutionPostureSummaryViewed(properties) {
    capture('execution_posture_summary_viewed', properties);
}
/** Emitted when the user selects an artifact tab — `tab_slug` only (FR-010). */
export function trackWorkItemArtifactTabSelected(properties) {
    capture('work_item_artifact_tab_selected', properties);
}
/** DC-486 — member chose a branch at repository scope confirmation (no branch names). */
export function trackRepositoryBranchSelected(properties) {
    capture('repository_branch_selected', properties);
}
export function trackWorkItemActivityTabViewed(properties) {
    capture('work_item_activity_tab_viewed', properties);
}
/** Tasks tab — metadata only (DC-336 / FR-010). */
export function trackWorkItemTasksTabViewed(properties) {
    capture('work_item_tasks_tab_viewed', properties);
}
export function trackWorkItemImplementTabViewed(properties) {
    capture('work_item_implement_tab_viewed', properties);
}
export function trackWorkItemReviewTabViewed(properties) {
    capture('work_item_review_tab_viewed', properties);
}
/** DC-334 — PostHog-safe: no comment body; optional opaque work_item_id only. */
export function trackSpecificationCommentSubmitted(properties = {}) {
    capture('specification_comment_submitted', properties);
}
/** DC-334 — enumerated transition only (`approve` | `request_changes`). */
export function trackSpecificationApprovalTransition(properties) {
    capture('specification_approval_transition', properties);
}
/** DC-335 — PostHog-safe: no comment body; optional opaque work_item_id only. */
export function trackTechnicalPlanCommentSubmitted(properties = {}) {
    capture('technical_plan_comment_submitted', properties);
}
/** DC-335 — enumerated transition only (`approve` | `request_changes`). */
export function trackTechnicalPlanApprovalTransition(properties) {
    capture('technical_plan_approval_transition', properties);
}
/** DC-337 — PostHog-safe: no comment body; optional opaque work_item_id only. */
export function trackTestPlanCommentSubmitted(properties = {}) {
    capture('test_plan_comment_submitted', properties);
}
/** DC-337 — enumerated transition only (`approve` | `request_changes`). */
export function trackTestPlanApprovalTransition(properties) {
    capture('test_plan_approval_transition', properties);
}
/** Tasks discussion — PostHog-safe: no comment body; optional opaque work_item_id only. */
export function trackTasksCommentSubmitted(properties = {}) {
    capture('tasks_comment_submitted', properties);
}
/** DC-371 — enumerated transition only (`approve` | `request_changes`). */
export function trackTasksApprovalTransition(properties) {
    capture('tasks_approval_transition', properties);
}
/** DC-388 — Govern standards catalog / coding-standard detail — org + surface key only. */
export function trackGovernStandardsCatalogViewed(properties) {
    capture('govern_standards_catalog_viewed', properties);
}
/** DC-388 — work-item applicability rail — org + opaque work_item_id only. */
export function trackWorkItemStandardsApplicabilityViewed(properties) {
    capture('work_item_standards_applicability_viewed', properties);
}
/** 412 — coarse engineering memory analytics (no proposal bodies). */
export function trackEngineeringMemoryFirstPracticesDetected(properties) {
    capture('engineering_memory_first_practices_detected', properties);
}
export function trackEngineeringMemoryProposalDecided(properties) {
    capture('engineering_memory_proposal_decided', properties);
}
export function trackEngineeringMemoryOnboardingBriefViewed(properties) {
    capture('engineering_memory_onboarding_brief_viewed', properties);
}
export function trackEngineeringMemorySummaryViewed(properties) {
    capture('engineering_memory_summary_viewed', properties);
}
/** DC-388 — routing requested — org + routing kind enum only (no policy bodies). */
export function trackGovernanceRoutingRequested(properties) {
    capture('governance_routing_requested', properties);
}
/**
 * Generic feature event tracker
 *
 * @param eventType - Type of feature event
 * @param properties - Event properties
 */
export function trackFeatureEvent(eventType, properties) {
    capture(eventType, properties);
}
//# sourceMappingURL=feature-events.js.map